import { Divider, MenuProps } from "antd";
import { ExportOutlined } from '@ant-design/icons';
import Dropdown, { type DropdownProps } from "antd/es/dropdown/dropdown";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import { IconButton } from "@/components/icon";
import { useLoginStateContext } from "@/pages/sys/login/providers/LoginStateProvider";
import { useRouter } from "@/router/hooks";
import { useUserActions, useUserInfo, useCreditsRemaining, useDaysRemaining, useTotalCreditsAllocated, useTier, useTierId, default as useUserStore } from "@/store/userStore";
import { useTheme } from "@/theme/hooks";
import { FiLogOut, FiArrowUpCircle } from "react-icons/fi";
import { IoRocketOutline } from "react-icons/io5";
import { RxLightningBolt } from "react-icons/rx";
import CancelRecurringModal from './CancelRecurringModal';


import { useSettings } from "@/store/settingStore";
import { ThemeMode } from "#/enum";

const HOMEPAGE = import.meta.env.VITE_APP_HOMEPAGE || "/dashboard/home";
const { VITE_AGENT_URL: AGENT_URL, VITE_APP_URL: _URL } = import.meta.env;

/**
 * Account Dropdown
 */
export default function AccountDropdown(props: { isBlocked?: boolean, isExpired?: boolean }) {
	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const { replace, push } = useRouter();
	const userInfo = useUserInfo() as any;
	const { name, email, id, user_id } = userInfo;
	const creditsRemaining = useCreditsRemaining();
	const dayRemaining = useDaysRemaining();
	const totalCreditsAllocated = useTotalCreditsAllocated();
	const tier = useTier();
	const tierId = useTierId();
	const isTrial = !tier || tierId === 1 || !['lite', 'standard', 'premium'].includes(tier.toLowerCase());
	const { clearUserInfoAndToken, refreshCredits } = useUserActions();
	const { backToLogin } = useLoginStateContext();
	const { t } = useTranslation();
	const role_id = localStorage.getItem("roleId");
	const isSuperAdmin = role_id === "1" || userInfo?.role_id === 1 || String(userInfo?.role_id) === "1";
	const { themeMode } = useSettings();

	useEffect(() => {
		const localUserId = localStorage.getItem('userId');
		const userId = id || user_id || localUserId || email;

		if (creditsRemaining === null && userId) {
			refreshCredits();
		}
	}, [creditsRemaining, id, user_id, email, refreshCredits]);

	const logout = () => {
		try {
			clearUserInfoAndToken();
			backToLogin();
		} catch (error) {
			console.log(error);
		} finally {
			if (window.location.pathname && window.location.pathname !== '/' && window.location.pathname !== '') {
				window.location.href = `${window.location.origin}/#/login`;
			} else {
				replace("/login");
			}
		}
	};
	const {
		themeVars: { colors, borderRadius, shadows },
	} = useTheme();

	const contentStyle: React.CSSProperties = {
		backgroundColor: colors.background.default,
		borderRadius: borderRadius.lg,
		boxShadow: shadows.dropdown,
	};

	const popupRender: DropdownProps["popupRender"] = () => (
		<div style={{ ...contentStyle, padding: "8px", width: "320px" }}>
			{/* Profile Info */}
			<div className="flex items-center gap-3 px-4 pt-4 pb-3">
				<div className="w-10 h-10 shrink-0 rounded-full bg-[#C41230] text-white flex items-center justify-center font-bold text-lg">
					{name ? name[0].toUpperCase() : "U"}
				</div>
				<div className="flex flex-col overflow-hidden">
					<div className={`font-bold text-[15px] truncate ${themeMode === ThemeMode.Dark ? 'text-white' : 'text-[#1E293B]'}`}>{name}</div>
					<div className="text-[#94A3B8] text-xs font-medium truncate">{email}</div>
				</div>
			</div>
			
			{!isSuperAdmin && (
				<>
					<Divider style={{ margin: "4px 0" }} />

					{/* Plan & Billing Section */}
					<div className="px-4 py-3">
						<div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${themeMode === ThemeMode.Dark ? 'text-gray-400' : 'text-[#94A3B8]'}`}>Plan & Billing</div>
						<div className={`border rounded-xl p-4 flex flex-col gap-3 ${themeMode === ThemeMode.Dark ? 'bg-[rgba(196,18,48,0.1)] border-[rgba(196,18,48,0.3)]' : 'bg-[#FFF1F2] border-[#FFE4E6]'}`}>
							<div className="flex items-center gap-2">
								{/* Free Trial Pill */}
								<div className={`flex items-center gap-1.5 px-2.5 py-1 border rounded-full ${themeMode === ThemeMode.Dark ? 'bg-transparent border-[rgba(196,18,48,0.3)]' : 'bg-white border-[#FFE4E6]'}`}>
									<RxLightningBolt className="text-[#C41230] text-[10px]" />
									<span className="text-[#C41230] text-[10px] font-bold capitalize">{String(role_id) === "4" ? "Enterprise" : (tier || 'Free Trial')}</span>
								</div>
								{/* Info Text */}
								<div className={`text-[10px] font-medium ${themeMode === ThemeMode.Dark ? 'text-gray-400' : 'text-[#64748B]'}`}>
									{!['lite', 'standard', 'premium'].includes((tier || '').toLowerCase()) && (
										<>{props.isExpired ? "0" : dayRemaining + 1} days left &middot; </>
									)}
									{creditsRemaining !== null && creditsRemaining <= 0 ? "0" : Math.round(Number(creditsRemaining))}/{totalCreditsAllocated !== null ? Math.round(Number(totalCreditsAllocated)) : "30"} credits
								</div>
							</div>
							{/* Upgrade Button */}
							{!["2", "3", "4"].includes(String(role_id)) && !["2", "3", "4"].includes(String(userInfo?.role_id)) && (
								<button 
									className="w-full flex items-center justify-center gap-2 bg-[#C41230] hover:bg-[#a00f27] transition-colors text-white text-xs font-bold py-2.5 rounded-lg"
									onClick={() => { push("/dashboard/upgrade-plan"); }}
								>
									<IoRocketOutline className="text-sm" />
									Upgrade plan
								</button>
							)}
							{/* Cancel Recurring Button */}
							{!isTrial && (
								<button 
									className={`w-full flex items-center justify-center gap-2 border transition-colors text-[#C41230] text-xs font-bold py-2.5 rounded-lg mt-1 ${themeMode === ThemeMode.Dark ? 'bg-transparent border-[rgba(196,18,48,0.3)] hover:bg-[rgba(196,18,48,0.2)]' : 'bg-white border-[#FFE4E6] hover:bg-[#FFE4E6]/50'}`}
									onClick={() => setCancelModalOpen(true)}
								>
									<ExportOutlined style={{fontSize:"12px"}}/>
									Cancel Recurring
								</button>
							)}
						</div>
					</div>
				</>
			)}

			<Divider style={{ margin: "4px 0" }} />

			{/* Logout Section */}
			<div className="px-4 py-3 pb-4">
				<button 
					className={`w-full flex items-center justify-center gap-2 border transition-colors text-[#C41230] text-sm font-bold py-2.5 rounded-lg ${themeMode === ThemeMode.Dark ? 'bg-transparent border-gray-700 hover:bg-gray-800' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
					onClick={logout}
				>
					<FiLogOut className="text-sm" strokeWidth={2.5} />
					Log out
				</button>
			</div>
		</div>
	);

	const items: MenuProps["items"] = [
		// {
		// 	label: (
		// 		<NavLink to="https://docs-admin.slashspaces.com/" target="_blank">
		// 			{t("sys.docs")}
		// 		</NavLink>
		// 	),
		// 	key: "0",
		// },
		// {
		// 	label: <NavLink to={HOMEPAGE}>{t("sys.menu.dashboard")}</NavLink>,
		// 	key: "1",
		// },
		{
			label: <NavLink to={"/dashboard/upgrade-plan"} style={{color:"#C41230", fontWeight: "600"}}>Upgrade plan</NavLink>,
			key: "1",
			icon: <FiArrowUpCircle style={{fontSize:"12px", color:"#C41230"}}/>
		},
		// {
		// 	label: <NavLink to="/profile">{t("sys.menu.user.profile")}</NavLink>,
		// 	key: "2",
		// },
		// {
		// 	label: <NavLink to="/account">{t("sys.menu.user.account")}</NavLink>,
		// 	key: "3",
		// },
		...(!isTrial ? [{
			label: (
				<button 
					className="font-bold w-full text-left flex items-center gap-2" 
					style={{color:"#C41230", padding: "6px 8px", backgroundColor: "rgba(196,18,48,0.08)", borderRadius: "6px"}}
					onClick={() => setCancelModalOpen(true)}
				>
					<ExportOutlined style={{fontSize:"14px", color:"#C41230"}}/>
					Cancel Recurring
				</button>
			),
			key: "cancel_recurring",
		}] : []),

		{ type: "divider" },
		{
			label: (
				<button className="font-bold text-warning" type="button">
					{t("sys.login.logout")}
				</button>
			),
			key: "4",
			onClick: logout,
		},
	];

	return (
		<>
			<Dropdown trigger={["click"]} popupRender={popupRender} disabled={props.isBlocked}>
				<IconButton className={`h-10 w-10 ${themeMode === ThemeMode.Dark ? 'bg-[#1E293B] text-white' : 'bg-[#F1F5F9] text-black'} transform-none px-0 hover:scale-105`} style={{ pointerEvents: props.isBlocked ? 'none' : 'auto' }}>
					<p>{name?.[0] || "U"}</p>
				</IconButton>
			</Dropdown>
			
			<CancelRecurringModal 
				open={cancelModalOpen} 
				onClose={() => setCancelModalOpen(false)} 
			/>
		</>
	);
}

